import { includes, isNil, omit } from 'lodash';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import styled, { css, useTheme } from 'styled-components';
import type { Portfolio } from 'venn-api';
import {
  getFund,
  getFundReturnsRange,
  getPrivateFund,
  getPrivatePortfolio,
  getSpecificPortfolioV3,
  getUploadedSeriesForFund,
  SupportedErrorCodes,
  updatePortfolioV3,
} from 'venn-api';
import {
  convertFundSeriesToUserData,
  DataUploaderModalWrapper,
  DataUploaderMode,
  PageContentContainer,
  PortfoliosContext,
  SideMenu,
  useMetaData,
  UserContext,
  type UserData,
  BulkManagementLegend,
  MAIN_CONTENT_ID,
} from 'venn-components';
import { ColorUtils, GetColor, Headline1, Icon, Tooltip, ZIndex } from 'venn-ui-kit';
import type { AnalysisSubjectType, ManageDataState, TemplateNavigationState } from 'venn-utils';
import {
  AnalysisSubject,
  analyticsService,
  assertExhaustive,
  FundUtils,
  getDefaultSection,
  invalidate,
  logExceptionIntoSentry,
  Routes,
  useApi,
  useHasFF,
  useModal,
  MANAGE_DATA_SECTION,
} from 'venn-utils';
import InvestmentManageData from './InvestmentManageData';
import ManageDataHeader from './ManageDataHeader';
import PortfolioManageData from './PortfolioManageData';
import { PrivateInvestmentManageData } from './PrivateInvestmentManageData';
import PrivatePortfolioManageData from './PrivatePortfolioManageData';
import { getMenu, getSection } from './sections';
import { useRecoilValue } from 'recoil';
import { analysisIdState, proxySetIdState } from 'venn-state';

const PAGE_MARGIN = 60;

interface ManageDataPageParams {
  objectType: AnalysisSubjectType;
  objectId: string;
  section?: MANAGE_DATA_SECTION;
}

const ManageDataPage: React.FC<React.PropsWithChildren<unknown>> = () => {
  const theme = useTheme();
  const hasPrivateAnalytics = useHasFF('private_analytics');
  const { hasPermission } = useContext(UserContext);
  const { updateMaster } = useContext(PortfoliosContext);
  const proxySetId = useRecoilValue(proxySetIdState);
  // TODO: VENN-23554 :Fix routing types
  const history = useHistory<ManageDataState & TemplateNavigationState>();
  const location = history.location;
  const { objectType, objectId, section } = useParams<ManageDataPageParams>();
  const { secondaryPortfolio, secondaryLabel } = location.state ?? {};
  const [subject, setSubject] = useState<AnalysisSubject>();
  const abortableGetPortfolio = useApi(getSpecificPortfolioV3);
  const abortableGetFund = useApi(getFund);
  const abortableUpdatePortfolio = useApi(updatePortfolioV3);

  const canOptimize = hasPermission('OPTIMIZATION');
  const hasHistoricals = useHasFF('historical_portfolios_ff');
  const showCashFlowPacingSettings = useHasFF('private_analytics');
  const hasTaxImpact = useHasFF('tax_impact_ff');
  const menu = useMemo(
    () =>
      getMenu(subject, {
        canOptimize,
        showCashFlowPacingSettings,
        hasHistoricals,
        hasTaxImpact,
      }),
    [subject, canOptimize, showCashFlowPacingSettings, hasHistoricals, hasTaxImpact],
  );
  const [selectedSection, setSelectedSection] = useState(getSection(menu, objectType, section));
  const analysisId = useRecoilValue(analysisIdState);
  const analysisIdRef = React.useRef(analysisId);

  useEffect(() => {
    if (objectType) {
      setSelectedSection(getSection(menu, objectType, section));
    }
  }, [menu, objectType, section]);

  // use when previous section shouldn't be part of the history, e.g. user has no access
  const redirectToSection = useCallback(
    (newSection: MANAGE_DATA_SECTION) => {
      history.replace(`${Routes.MANAGE_DATA_PATH}/${objectType}/${objectId}/${newSection}`, location.state);
    },
    [history, objectType, objectId, location.state],
  );

  const switchSection = useCallback(
    (newSection: MANAGE_DATA_SECTION) => {
      analyticsService.navigationTriggered({
        destinationPageTitle: newSection,
        itemType: 'tab',
        location: 'Manage Data Page',
        userIntent: 'Change tabs',
      });
      history.replace(
        `${Routes.MANAGE_DATA_PATH}/${objectType}/${objectId}/${newSection}`,
        // don't include when switching sections so that there's no animation
        omit(location.state, 'previousPathMatch'),
      );

      const mainContentElement = document.getElementById(MAIN_CONTENT_ID);
      if (mainContentElement) {
        mainContentElement.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      } else {
        logExceptionIntoSentry(new Error('Main content element not found'));
      }
    },
    [history, objectType, objectId, location.state],
  );

  useEffect(() => {
    // make sure user is in a valid section, i.e. section is in `menu`
    // also ensure subject has loaded before checking `menu` so that we know if TABS.DATA is accessible or not
    if (!section || (subject && !menu.find(({ value }) => value === section))) {
      redirectToSection(getDefaultSection(objectType));
    }
  }, [redirectToSection, objectType, section, menu, subject]);

  const fetchSubject = useCallback(async () => {
    try {
      if (objectType === 'private-portfolio') {
        const portfolio = (await getPrivatePortfolio(objectId)).content;
        setSubject(new AnalysisSubject(portfolio, 'private-portfolio', proxySetId));
        return;
      }
      if (objectType === 'private-investment') {
        const investment = (await getPrivateFund(objectId)).content;
        setSubject(new AnalysisSubject(investment, 'private-investment', proxySetId));
        return;
      }

      if (objectType === 'portfolio') {
        const portfolio = (await abortableGetPortfolio(Number(objectId), undefined, proxySetId)).content;
        setSubject(
          new AnalysisSubject(portfolio, 'portfolio', proxySetId, {
            secondaryPortfolio,
            secondaryLabel,
          }),
        );
        return;
      }
      if (objectType === 'investment') {
        const investment = (await abortableGetFund(objectId, proxySetId)).content;
        setSubject(new AnalysisSubject(investment, 'investment', proxySetId));
        return;
      }
    } catch (e) {
      // 'AbortError' is expected as fetches for the same subject will abort the older requests
      // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
      if (e.name === 'AbortError') {
        return;
      }
      // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
      logExceptionIntoSentry(e);
    }
    // handle both fetch subject exception and invalid object type
    const errorQueryParam = 'invalidSubject';
    history.replace(`${Routes.HOME_PATH}?${errorQueryParam}=true`);
  }, [
    abortableGetFund,
    abortableGetPortfolio,
    history,
    objectId,
    objectType,
    secondaryLabel,
    secondaryPortfolio,
    proxySetId,
  ]);

  const updatePortfolio = useCallback(
    async (portfolio: Portfolio) => {
      try {
        const updatedPortfolio = portfolio.master
          ? (await abortableUpdatePortfolio(portfolio.id, proxySetId ?? undefined, portfolio)).content
          : await updateMaster(portfolio);
        updatedPortfolio &&
          setSubject(
            new AnalysisSubject(updatedPortfolio, 'portfolio', proxySetId, {
              secondaryPortfolio,
              secondaryLabel,
            }),
          );
      } catch (e) {
        // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
        logExceptionIntoSentry(e);
      }
    },
    [abortableUpdatePortfolio, proxySetId, secondaryLabel, secondaryPortfolio, updateMaster],
  );

  const shouldFetch = useMemo(() => {
    if (analysisId !== analysisIdRef.current) {
      return true;
    }

    if (!subject || subject.type !== objectType) {
      return true;
    }

    switch (objectType) {
      case 'portfolio': {
        return objectId !== String(subject?.portfolio?.id ?? 0);
      }
      case 'investment': {
        return objectId !== subject?.fund?.id;
      }
      case 'private-investment': {
        return objectId !== subject?.privateFund?.id;
      }
      case 'private-portfolio': {
        return objectId !== subject?.privatePortfolio?.id;
      }
      default: {
        throw assertExhaustive(objectType, 'Unexpected subject type');
      }
    }
  }, [subject, objectType, objectId, analysisId]);

  useEffect(() => {
    if (shouldFetch) {
      fetchSubject();
      analysisIdRef.current = analysisId;
    }
  }, [fetchSubject, shouldFetch, analysisId]);

  const onFundUpdated = useCallback(
    (id: string) => {
      if (subject?.id === id) {
        fetchSubject();
      }
    },
    [fetchSubject, subject],
  );

  const [investmentUserData, setInvestmentUserData] = useState<UserData>();
  const [investmentHasNoReturns, setInvestmentHasNoReturns] = useState(false);
  const fetchFundSeries = useCallback(async () => {
    if (isNil(objectId) || !subject?.fund) {
      return;
    }

    try {
      await getFundReturnsRange(true, subject.fund.id, proxySetId);
      if (!FundUtils.isPerformanceExportable(subject.fund)) {
        return;
      }

      const { content } = await getUploadedSeriesForFund(objectId, proxySetId);
      setInvestmentUserData(convertFundSeriesToUserData(subject.fund.name, content));
    } catch (error) {
      // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
      if (error.status === 404 && error.content.code === SupportedErrorCodes.NoFundReturns) {
        /* Change the view if the fund has no returns */
        setInvestmentHasNoReturns(true);
        return;
      }
      // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
      logExceptionIntoSentry(error);
    }
  }, [objectId, subject, proxySetId]);

  useEffect(() => {
    fetchFundSeries();
  }, [fetchFundSeries]);

  const useMetaDataReturn = useMetaData(subject?.fund?.id);

  /* If the fund has no returns the modal is used to upload returns. */
  const [isUploadModalOpen, openUploadModal, closeUploadModal] = useModal();

  /* Handle behavior of return modal being closed.
   * If the currently selected investment was uploaded re-fetch return data. */
  const onUploadComplete = (portfolio: Portfolio) => {
    /* Determine if the current investment was uploaded. */
    const uploadedFundIds = portfolio.children.map((p) => p.fund?.id);
    if (includes(uploadedFundIds, objectId)) {
      setInvestmentHasNoReturns(false);
      fetchFundSeries();
    }
    closeUploadModal();
  };

  const handleFundUpdated = useCallback(async () => {
    await Promise.all([
      fetchSubject(),
      subject?.id && invalidate('investment', subject.id),
      useMetaDataReturn.refreshMetadata(),
    ]);
  }, [fetchSubject, subject?.id, useMetaDataReturn]);

  const showBackButton = !!location.state; // show back button if the prior state is set in the app

  return (
    <>
      <HeaderWrapper>
        <PageTitle refreshedStyling>
          {showBackButton ? (
            <LinkWrapper refreshedStyling>
              <Tooltip content="Back" usePortal>
                <div data-testid="back">
                  <Icon type="arrow-left" onClick={history.goBack} />
                </div>
              </Tooltip>
            </LinkWrapper>
          ) : null}
          Manage Data
        </PageTitle>
        <ManageDataHeader
          subject={subject}
          onNameUpdated={handleFundUpdated}
          investmentUserData={investmentUserData}
          investmentUseMetaDataReturn={useMetaDataReturn}
        />
      </HeaderWrapper>
      <MainContainer>
        <TabsContainer refreshedStyling>
          <SideMenu<MANAGE_DATA_SECTION>
            refreshedStyling
            items={menu}
            selectedItems={selectedSection}
            onClick={switchSection}
            maxHeight={undefined}
            containerBackgroundColor={theme.Colors.GreyScale.Grey10}
            allowItemOverflow
          />
          {selectedSection === MANAGE_DATA_SECTION.ANALYSIS_RANGE && !objectType.startsWith('private') && (
            <BulkManagementLegend isHistorical={!!subject?.portfolio?.historical} />
          )}
        </TabsContainer>
        <PageContentWrapper minHeight={700}>
          <PageContentContainer className="page-content" refreshedStyling>
            {hasPrivateAnalytics && objectType === 'private-investment' ? (
              <PrivateInvestmentManageData subject={subject} selectedSection={selectedSection} />
            ) : hasPrivateAnalytics && objectType === 'private-portfolio' ? (
              <PrivatePortfolioManageData portfolio={subject?.privatePortfolio} selectedSection={selectedSection} />
            ) : objectType === 'investment' && selectedSection ? (
              <InvestmentManageData
                subject={subject}
                onFundUpdated={handleFundUpdated}
                selectedSection={selectedSection}
                userData={investmentUserData}
                useMetaDataReturn={useMetaDataReturn}
                updateUserData={setInvestmentUserData}
                missingReturns={investmentHasNoReturns}
                openUploadModal={openUploadModal}
              />
            ) : objectType === 'portfolio' ? (
              <PortfolioManageData
                subject={subject}
                onFundUpdated={onFundUpdated}
                selectedSection={selectedSection}
                updatePortfolio={updatePortfolio}
              />
            ) : null}
          </PageContentContainer>
        </PageContentWrapper>
      </MainContainer>
      {isUploadModalOpen && (
        <DataUploaderModalWrapper
          mode={DataUploaderMode.Returns}
          onCancel={closeUploadModal}
          onComplete={onUploadComplete}
          customUploadReviewButtonLabel="Upload"
        />
      )}
    </>
  );
};

export default ManageDataPage;

const HeaderWrapper = styled.div`
  z-index: ${ZIndex.StickyFront};
  background-color: ${GetColor.White};
  position: sticky;
  top: 0;
`;

const MainContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  padding: 0 60px 46px;
`;

const TabsContainer = styled.div<{ refreshedStyling?: boolean }>`
  position: sticky;
  top: ${(props) => (props.refreshedStyling ? '188px' : '248px')};
  align-self: flex-start;

  flex: 0 0 200px;
  padding-top: ${(props) => (props.refreshedStyling ? '28px' : '40px')};

  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const PageContentWrapper = styled.div<{ minHeight?: number }>`
  padding-left: 20px;
  flex: 1;
  ${(props) => props.minHeight && `min-height: ${props.minHeight}px;`}
`;

const LinkWrapper = styled.div<{ refreshedStyling?: boolean }>`
  display: inline;

  ${({ refreshedStyling }) =>
    refreshedStyling
      ? css`
          margin-right: 8px;
          font-size: 30px;
          padding: 0 4px;
          color: ${GetColor.DarkBlue};
        `
      : css`
          margin-right: 20px;
          font-size: 36px;
        `}
  &:hover {
    background-color: ${ColorUtils.opacifyFrom(GetColor.Primary.Dark, 0.1)};
    color: ${GetColor.Primary.Dark};
  }

  i {
    font-weight: normal;
  }
`;

const PageTitle = styled(Headline1)<{ refreshedStyling?: boolean }>`
  position: sticky;
  margin: 0 ${PAGE_MARGIN}px;

  ${({ refreshedStyling }) =>
    refreshedStyling
      ? css`
          padding: 16px 0;
          border-bottom: 1px solid ${GetColor.GreyScale.Grey30};
          font-size: 32px;
          font-weight: 400;
        `
      : css`
          padding: 46px 0 15px;
          border-bottom: 2px solid ${GetColor.GreyScale.Grey20};
        `}
`;
